import { Controller } from '@hotwired/stimulus';
import Sortable from 'sortablejs';
import { postRequest } from '../../src/utils';

// Used in: rearrange_lesson_plan.html.erb
// Purpose: Rearrange the order of the lessons for each lesson order (week)
// Explanation: The lesson plan is made up of several lesson orders. Each lesson order representing one week. This controller is used to allow the user to rearrange the order of the lessons for each lesson order (week) either through drag and drop (custom), or the 'fixed' and 'rotating' buttons. At first, the current lesson orders are stored by their lesson order id originalLessonPlan value (array). For custom arrangement, the final order is retrieved again through the order of the targets. For fixed or rotating, the final lesson plan is directly stored and saved. Upon save, the pairs to be updated are sent to the backend through a post request.

// Connects to data-controller="timetable--rearrange"
export default class extends Controller {
  static targets = ['sortable', 'lesson', 'message'];
  static values = {
    originalLessonPlan: Array,
    finalLessonPlan: Array,
    pairsToUpdate: Array,
    sortables: Array,
  };

  connect() {
    console.log('timetable rearrange controller connected');
    this.createSortable();
    this.originalLessonPlanValue = this.getLessonPlan();
  }

  // Add sortable drag and drop to lesson orders within lesson plans
  createSortable() {
    const options = {
      ghostClass: 'ghost',
      dragClass: 'bg-blue-300',
      chosenClass: 'bg-blue-300',
      animation: 150,
    };
    this.sortableTargets.forEach((list) => {
      this.sortablesValue.push(Sortable.create(list, options));
    });
  }

  // Get lesson and corresponding band id pairs of the lesson plan
  getLessonPlan() {
    let lessonPlan = [];
    // console.log(this.lessonTargets);
    this.lessonTargets.forEach((lesson) => {
      let { lessonId, bandId, bandLetter, week } = lesson.dataset;
      let pair = { lessonId, bandId, bandLetter, week };
      lessonPlan.push(pair);
    });
    // console.log('lesson plan', lessonPlan);
    return lessonPlan;
  }

  // Sort the lessons by week and band letter
  sortByWeekAndBandLetter(arr) {
    arr.sort((a, b) => {
      if (a.week !== b.week) {
        return a.week - b.week;
      } else {
        return a.bandLetter.localeCompare(b.bandLetter);
      }
    });
    return arr;
  }

  // Group the lessons by week, each week being one array
  groupByWeek(arr) {
    const groups = {};
    arr.forEach((item) => {
      if (!groups[item.week]) {
        groups[item.week] = [item];
      } else {
        groups[item.week].push(item);
      }
    });
    return Object.values(groups);
  }

  // Move the last item in the array to the front x amount of times
  moveLastToFront(arr, times) {
    if (arr.length > 0 && times > 0) {
      for (let i = 0; i < times; i++) {
        let last = arr.pop();
        arr.unshift(last);
      }
    }
    return arr;
  }

  // Order the lessons by week and band letter and then rotate them by 1 each week
  rotating() {
    let lessonPlan = this.originalLessonPlanValue;

    // Sort the lessons by week and band later (fixed schedule), then turn the weeks into groups (arrays) within one lesson plan array
    lessonPlan = this.sortByWeekAndBandLetter(lessonPlan);
    let grouped = this.groupByWeek(lessonPlan);

    // For each week, rotate the lessons by 1
    let rotated = [];
    grouped.forEach((week, index) => {
      rotated.push(this.moveLastToFront(week, index));
    });

    this.finalLessonPlanValue = rotated.flat(1);
    // Save as 'not custom' so that the final lesson plan is used
    this.updateHTML();
  }

  // Order the lesson plan into a fixed schedule (band a is always first, band b is always second etc)
  fixed() {
    console.log('fixed');
    let lessonPlan = this.originalLessonPlanValue;
    lessonPlan = this.sortByWeekAndBandLetter(lessonPlan);
    this.finalLessonPlanValue = lessonPlan;
    // this.save(false);
    this.updateHTML();
  }

  updateHTML() {
    this.finalLessonPlanValue.forEach((lesson) => {
      const lessonElement = document.querySelector(`[data-lesson-id="${lesson.lessonId}"]`);
      const parentElement = lessonElement.closest('ol');
      parentElement.appendChild(lessonElement);
    });
  }

  // Compare original and final lesson plans
  comparePlans() {
    this.finalLessonPlanValue = this.getLessonPlan();
    let diff = [];
    this.originalLessonPlanValue.forEach((pair, index) => {
      this.finalLessonPlanValue.forEach((fPair, fIndex) => {
        if (index == fIndex) {
          if (pair.bandId !== fPair.bandId || pair.lessonId !== fPair.lessonId)
            diff.push({ lesson_id: pair.lessonId, band_id: fPair.bandId });
        }
      });
    });
    console.log('diff', diff);
    this.pairsToUpdateValue = diff;
  }

  // Post updated lesson plans to db and receive turbostream html over the wire
  async updatePairs() {
    const context = this;
    try {
      let resp = await postRequest('/admin/lessons/batch_update_bands', {
        pairs: context.pairsToUpdateValue,
      });
      let html = await resp.text();
      await Turbo.renderStreamMessage(html);
    } catch (error) {
      throw error;
    }
  }

  // On click of save btn, compare lesson plans before and after, then post to db, then update html
  async save() {
    this.comparePlans();
    if (this.pairsToUpdateValue.length > 0) {
      try {
        await this.updatePairs();
      } catch (error) {
        console.log(error);
        window.alert('Something went wrong, please try again later');
      }
    } else {
      this.messageTarget.textContent = 'No changes to save';
    }
  }

  disconnect() {
    this.sortablesValue.forEach((sortable) => sortable.destroy());
  }
}
